import React, { useState } from 'react';
import { Volume2, VolumeX, Play, Pause, Disc, Info, Activity, Thermometer, Music, Sliders } from 'lucide-react';
import { AudioEngine } from '../audio/AudioEngine';

interface HeaderProps {
  activeReport: 'ar6' | 'sr15';
  onSelectReport: (report: 'ar6' | 'sr15') => void;
  isGlobalPlaying: boolean;
  onToggleGlobalPlay: () => void;
  onOpenLegend: () => void;
  comparativeWarming: '1.5' | '2.0' | null;
  onToggleComparativeWarming: (val: '1.5' | '2.0') => void;
}

export const Header: React.FC<HeaderProps> = ({
  activeReport,
  onSelectReport,
  isGlobalPlaying,
  onToggleGlobalPlay,
  onOpenLegend,
  comparativeWarming,
  onToggleComparativeWarming
}) => {
  const audioEngine = AudioEngine.getInstance();
  const [volume, setVolume] = useState<number>(audioEngine.getMasterVolume());
  const [isMuted, setIsMuted] = useState<boolean>(audioEngine.getIsMuted());
  const [isMusicalMode, setIsMusicalMode] = useState<boolean>(audioEngine.getIsMusicalMode());

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    audioEngine.setMasterVolume(val);
    if (isMuted && val > 0) {
      setIsMuted(audioEngine.toggleMute());
    }
  };

  const handleToggleMute = () => {
    const muted = audioEngine.toggleMute();
    setIsMuted(muted);
  };

  const handleToggleMusicalMode = () => {
    const enabled = audioEngine.toggleMusicalMode();
    setIsMusicalMode(enabled);
  };

  const handlePlayReferenceTone = async () => {
    await audioEngine.unlock();
    audioEngine.playReferenceTone(2.0);
  };

  return (
    <header className="w-full glass-panel sticky top-0 z-40 border-b border-slate-800/80 px-4 lg:px-8 py-3.5 flex flex-wrap items-center justify-between gap-4">
      {/* Title & Report Switcher */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 text-slate-950 font-black flex-shrink-0">
          <Activity className="w-6 h-6 animate-pulse" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg lg:text-xl font-bold bg-gradient-to-r from-white via-slate-200 to-cyan-300 bg-clip-text text-transparent">
              IPCC Sonification Suite
            </h1>
          </div>

          {/* Top-Level Report Switcher */}
          <div className="flex items-center gap-1.5 mt-1">
            <button
              onClick={() => onSelectReport('ar6')}
              className={`px-2.5 py-0.5 text-xs font-mono font-bold rounded-lg transition-all border ${
                activeReport === 'ar6'
                  ? 'bg-cyan-500 text-slate-950 border-cyan-400 glow-cyan shadow'
                  : 'bg-slate-900/80 text-slate-400 border-slate-800 hover:text-slate-200'
              }`}
            >
              AR6 WGI SPM (2021)
            </button>
            <button
              onClick={() => onSelectReport('sr15')}
              className={`px-2.5 py-0.5 text-xs font-mono font-bold rounded-lg transition-all border ${
                activeReport === 'sr15'
                  ? 'bg-amber-500 text-slate-950 border-amber-400 glow-amber shadow'
                  : 'bg-slate-900/80 text-slate-400 border-slate-800 hover:text-slate-200'
              }`}
            >
              SR15 SPM (2018)
            </button>
          </div>
        </div>
      </div>

      {/* Center & Right Controls */}
      <div className="flex items-center flex-wrap gap-3 lg:gap-4">
        {/* Global Musical Mode vs Scientific Microtonal Mode Toggle */}
        <button
          onClick={handleToggleMusicalMode}
          title="Toggle Musical Scale Quantization Mode vs Scientific Continuous Microtonal Mode"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-mono font-bold transition-all shadow-sm ${
            isMusicalMode
              ? 'bg-gradient-to-r from-indigo-950 to-purple-950 text-purple-300 border-purple-500/60 glow-purple'
              : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-slate-200'
          }`}
        >
          <Music className="w-4 h-4 text-purple-400" />
          <span>{isMusicalMode ? 'Musical Mode (Quantized Scale)' : 'Scientific Mode (Microtonal)'}</span>
        </button>

        {/* Comparative 1.5°C vs 2.0°C A/B Button for SR15 */}
        {activeReport === 'sr15' && (
          <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs">
            <span className="text-[10px] text-slate-400 font-mono px-1 flex items-center gap-1">
              <Thermometer className="w-3 h-3 text-amber-400" /> A/B Test:
            </span>
            <button
              onClick={() => onToggleComparativeWarming('1.5')}
              className={`px-2.5 py-1 rounded-lg font-mono font-bold transition-all ${
                comparativeWarming === '1.5'
                  ? 'bg-cyan-500 text-slate-950 glow-cyan'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              1.5°C
            </button>
            <button
              onClick={() => onToggleComparativeWarming('2.0')}
              className={`px-2.5 py-1 rounded-lg font-mono font-bold transition-all ${
                comparativeWarming === '2.0'
                  ? 'bg-red-500 text-slate-950 glow-red'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              2.0°C
            </button>
          </div>
        )}

        {/* Reference Pitch Anchor Button */}
        <button
          onClick={handlePlayReferenceTone}
          title="Play 0.0°C Reference Tone (261.63 Hz / C4)"
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900/90 border border-slate-700/70 hover:border-cyan-500/50 hover:bg-slate-800 text-xs font-mono text-cyan-300 transition-all shadow-sm group"
        >
          <Disc className="w-4 h-4 text-cyan-400 group-hover:rotate-180 transition-transform duration-500" />
          <span>Ref Tone: 0.0°C (C4)</span>
        </button>

        {/* Global Play/Pause */}
        <button
          onClick={onToggleGlobalPlay}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-lg font-semibold text-xs transition-all shadow-lg ${
            isGlobalPlaying
              ? 'bg-amber-500 hover:bg-amber-400 text-slate-950 glow-amber'
              : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 glow-cyan'
          }`}
        >
          {isGlobalPlaying ? (
            <>
              <Pause className="w-4 h-4 fill-current" />
              <span>Pause Timeline</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4 fill-current" />
              <span>Play Active Figure</span>
            </>
          )}
        </button>

        {/* Acoustic Legend Trigger */}
        <button
          onClick={onOpenLegend}
          className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 hover:text-white transition-colors"
          title="Acoustic Mapping Legend"
        >
          <Info className="w-4 h-4" />
        </button>

        {/* Volume & Mute Controls */}
        <div className="flex items-center gap-2.5 bg-slate-900/80 px-3 py-1.5 rounded-lg border border-slate-800">
          <button
            onClick={handleToggleMute}
            className="text-slate-400 hover:text-cyan-400 transition-colors"
            title={isMuted ? 'Unmute Master' : 'Mute Master'}
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="w-4 h-4 text-red-400" />
            ) : (
              <Volume2 className="w-4 h-4 text-cyan-400" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-20 lg:w-28 accent-cyan-400 cursor-pointer h-1.5 rounded-lg bg-slate-800"
          />
          <span className="text-[10px] font-mono text-slate-400 w-7 text-right">
            {isMuted ? '0%' : `${Math.round(volume * 100)}%`}
          </span>
        </div>
      </div>
    </header>
  );
};
